import React, { PureComponent } from "react";
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import PropTypes from "prop-types";
import numeral from "numeral";
import startCase from "lodash/startCase";
import { currencyPosition } from "../../utils/currency-position";
import "./areaChart.scss";
export class AreaChart extends PureComponent {
  static defaultProps = {
    hasRawData: false,
    showDecimals: false,
    interval: "preserveEnd",
    right: 30,
    left: 30,
    hideLegend: false,
    height: 300,
    strokeDasharray: "3 3",
    xTickStyle: { stroke: "none", fill: "#5b606a" },
    yTickStyle: { stroke: "none", fill: "#5b606a" },
    renderChildren: null
  };

  static propTypes = {
    chartData: PropTypes.array,
    width: PropTypes.number,
    height: PropTypes.number,
    hideCurrency: PropTypes.bool,
    hideLegend: PropTypes.bool,
    currencySymbol: PropTypes.string,
    tooltipName: PropTypes.string,
    hasRawData: PropTypes.bool,
    showDecimals: PropTypes.bool,
    interval: PropTypes.any,
    right: PropTypes.number,
    left: PropTypes.number,
    strokeDasharray: PropTypes.string,
    margin: PropTypes.object,
    renderChildren: PropTypes.func,
    toolTipContent: PropTypes.bool,
    xTickStyle: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    yTickStyle: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    cartesianGridVerticalPoints: PropTypes.func
  };

  formatter = (value) => {
    const formattedNumber = numeral(value).format("0.a");

    if (this.props.currencySymbol === "%") {
      return `${value}%`;
    }

    return currencyPosition(formattedNumber, this.props.currencySymbol);
  };

  toolTipFormatter = (value, name) => {
    const { tooltipName } = this.props;
    const formattedNumber = numeral(value).format("0,0");
    const singleSeriesName = this.isLastYearEmpty() && tooltipName ? tooltipName : startCase(name);

    if (this.props.currencySymbol === "%") {
      return [`${value}%`, singleSeriesName];
    }

    return [currencyPosition(formattedNumber, this.props.currencySymbol), singleSeriesName];
  };

  legendFormatter = (value, entry) => {
    return <span style={{ color: entry.color }}>{startCase(value)}</span>;
  };

  handleClick = (e) => {
    if (this.props.hasRawData) {
      window.open(`${e.activePayload[0].payload.rawDataUrl}`, "_blank");
    }
  };

  isLastYearEmpty() {
    if (!this.props.chartData) {
      return !this.props.chartData;
    }

    const lastYear = this.props.chartData.map((val) => {
      return val.lastYear;
    });

    return Math.max.apply(null, lastYear) === 0;
  }

  renderCustomTooltip() {
    const CustomTooltip = ({ active, payload, label, contentStyle, labelStyle }) => {
      const finalItemStyle = {
        display: "block",
        paddingTop: 4,
        paddingBottom: 4,
        color: "#0099ce"
      };

      const finalStyleWrapper = {
        margin: 0,
        padding: 10,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        whiteSpace: "nowrap",
        ...contentStyle
      };

      if (active && payload && payload.length) {
        let [value, name] = this.toolTipFormatter(payload[0].value, this.props.tooltipName);
        return (
          <div className="recharts-default-tooltip" style={finalStyleWrapper}>
            <p
              className="recharts-tooltip-label"
              style={{ ...labelStyle, margin: 0 }}
            >{`${label}`}</p>
            <div className="recharts-tooltip-item" style={finalItemStyle}>
              <span className="recharts-tooltip-item-name">{name}</span>
              <span className="recharts-tooltip-item-separator"> : </span>
              <span className="recharts-tooltip-item-value">{value}</span>
              <p style={{ color: "0099ce", margin: 0 }}>
                {payload[0].payload.additionalTooltip +
                  (this.props.currencySymbol ? this.props.currencySymbol : "")}
              </p>
            </div>
          </div>
        );
      }

      return null;
    };

    return CustomTooltip;
  }

  render() {
    const { width, height, margin, cartesianGridVerticalPoints } = this.props;
    const verticalPoints =
      typeof cartesianGridVerticalPoints === "function"
        ? cartesianGridVerticalPoints(width)
        : undefined;
    const chartStyle = {
      width,
      height,
      margin
    };

    return (
      <ComposedChart data={this.props.chartData} onClick={this.handleClick} {...chartStyle}>
        <CartesianGrid
          verticalPoints={verticalPoints}
          strokeDasharray={this.props.strokeDasharray}
          stroke="#696969"
          allowDecimals
        />
        <XAxis
          dataKey="name"
          tick={this.props.xTickStyle}
          allowDecimals
          interval={this.props.interval}
        />
        <YAxis yAxisId="left" tickFormatter={this.formatter} tick={this.props.yTickStyle} />
        <Tooltip
          formatter={this.toolTipFormatter}
          content={this.props.toolTipContent && this.renderCustomTooltip()}
          contentStyle={{ backgroundColor: "#353942", borderColor: "#484b52" }}
          labelStyle={{ color: "#ccc" }}
        />
        {!this.props.hideLegend && <Legend formatter={this.legendFormatter} />}
        {!this.isLastYearEmpty() && (
          <Area yAxisId="left" type="monotone" dataKey="lastYear" stroke="#42b77a" fill="#42b77a" />
        )}
        <Area yAxisId="left" type="monotone" dataKey="thisYear" stroke="#0099ce" fill="#0099ce" />
        {this.props.renderChildren && this.props.renderChildren()}
      </ComposedChart>
    );
  }
}

export default AreaChart;
